import { defineComponent as _defineComponent } from 'vue'
import { createElementVNode as _createElementVNode, unref as _unref, resolveComponent as _resolveComponent, createVNode as _createVNode, createTextVNode as _createTextVNode, withKeys as _withKeys, withModifiers as _withModifiers, openBlock as _openBlock, createElementBlock as _createElementBlock, createCommentVNode as _createCommentVNode, toDisplayString as _toDisplayString, pushScopeId as _pushScopeId, popScopeId as _popScopeId } from "vue"

const _withScopeId = n => (_pushScopeId("data-v-6b11aaf8"),n=n(),_popScopeId(),n)
const _hoisted_1 = { class: "flex flex-col gap-y-6 py-4" }
const _hoisted_2 = ["onKeydown", "onDrop"]
const _hoisted_3 = { class: "text-primary30 mb-4" }
const _hoisted_4 = /*#__PURE__*/ _withScopeId(() => /*#__PURE__*/_createElementVNode("p", { class: "text-[16px] text-textPrimary text-center" }, [
  /*#__PURE__*/_createTextVNode(" Arraste o arquivo ou "),
  /*#__PURE__*/_createElementVNode("span", { class: "text-primary20 underline" }, "clique aqui"),
  /*#__PURE__*/_createTextVNode(" para selecionar ")
], -1))
const _hoisted_5 = /*#__PURE__*/ _withScopeId(() => /*#__PURE__*/_createElementVNode("div", { class: "mt-4 text-center text-[14px] text-primary30" }, [
  /*#__PURE__*/_createElementVNode("p", null, "Formato aceito: .csv"),
  /*#__PURE__*/_createElementVNode("p", null, "Máximo de 10.000 linhas e 5 MB")
], -1))
const _hoisted_6 = {
  key: 1,
  "data-testid": "import-file-loaded",
  class: "dropzone-border relative rounded-[24px] bg-primary5/30 p-8 flex flex-col items-center justify-center gap-4"
}
const _hoisted_7 = { class: "text-primary30" }
const _hoisted_8 = { class: "flex flex-col items-center gap-0.5" }
const _hoisted_9 = { class: "text-sm font-bold text-textPrimary truncate max-w-full" }
const _hoisted_10 = { class: "text-xs text-neutralLight" }
const _hoisted_11 = {
  key: 0,
  class: "w-full text-center"
}
const _hoisted_12 = { class: "text-sm font-semibold text-primary30" }
const _hoisted_13 = { class: "text-xs text-neutralLight mt-2" }
const _hoisted_14 = { key: 1 }

import { ref, computed, h } from 'vue'
import { mdiTrayArrowUp, mdiCloseCircleOutline, mdiFileDelimited } from '@mdi/js'
import { useToast } from 'vue-toastification'
import type { QualificationPolicyType } from './types'
import { DEFAULT_POLICY_TYPE } from './types'

const MAX_FILE_SIZE = 5 * 1024 * 1024 // 5MB
const MAX_LINES = 10_000

interface FileAnalysis {
  lineCount: number
  headerLine: string
}


export default /*#__PURE__*/_defineComponent({
  __name: 'StepFile',
  props: {
    modelValue: {},
    uploading: { type: Boolean, default: false },
    uploadProgress: { default: 0 },
    uploadLoaded: { default: 0 },
    uploadTotal: { default: 0 },
    policyType: { default: DEFAULT_POLICY_TYPE }
  },
  emits: ["update:modelValue"],
  setup(__props: any, { emit }) {

const props = __props;



const uploadProgressLabel = computed(
  () => `${formatSize(props.uploadLoaded)} de ${formatSize(props.uploadTotal)}`
)



const fileInput = ref<HTMLInputElement | null>(null)
const toast = useToast()

const UF_ALIASES = new Set(['uf', 'estado', 'state', 'crm_uf', 'uf_crm'])

const BANK_DATA_ALIASES = new Set([
  'dados_bancarios',
  'dados bancarios',
  'banco',
  'bank',
  'bank_data',
  'has_bank_data'
])
const PAYMENT_CRITERIA_ALIASES = new Set([
  'criterio_pagamento',
  'criterio pagamento',
  'pagamento',
  'payment',
  'payment_criteria',
  'has_payment_criteria'
])

const REGISTRATION_FORM_ALIASES = new Set([
  'cadastro_medico',
  'cadastro medico',
  'cadastro',
  'registration_form',
  'has_registration_form'
])
const CONTRACT_ALIASES = new Set(['contrato', 'contract', 'has_contract'])
const DOCUMENTATION_ALIASES = new Set(['documentacao', 'documentation', 'has_documentation'])

const TRANSFER_VALIDATION_SETS = [BANK_DATA_ALIASES, PAYMENT_CRITERIA_ALIASES]
const DOCUMENTAL_VALIDATION_SETS = [
  REGISTRATION_FORM_ALIASES,
  CONTRACT_ALIASES,
  DOCUMENTATION_ALIASES
]

const getValidationSets = () =>
  props.policyType === 'DOCUMENTARY_QUALIFICATION'
    ? DOCUMENTAL_VALIDATION_SETS
    : TRANSFER_VALIDATION_SETS

const getValidationColumnNames = (): string[] =>
  props.policyType === 'DOCUMENTARY_QUALIFICATION'
    ? ['cadastro_medico', 'contrato', 'documentacao']
    : ['dados_bancarios', 'criterio_pagamento']

const triggerFileInput = () => {
  fileInput.value?.click()
}

const handleFileChange = (e: Event) => {
  const target = e.target as HTMLInputElement
  if (target.files?.length) processFile(target.files[0])
}

const handleFileDrop = (e: DragEvent) => {
  if (e.dataTransfer?.files.length) processFile(e.dataTransfer.files[0])
}

const analyzeFile = (file: File): Promise<FileAnalysis> => {
  return new Promise((resolve, reject) => {
    const reader = new FileReader()
    reader.onload = () => {
      const text = reader.result as string
      const firstNewline = text.indexOf('\n')
      const headerLine = (firstNewline === -1 ? text : text.substring(0, firstNewline)).trim()

      const lines = text.split('\n')
      let lineCount = 0
      for (let i = 1; i < lines.length; i++) {
        if (lines[i].trim()) lineCount++
      }

      resolve({ lineCount, headerLine })
    }
    reader.onerror = () => reject(reader.error)
    reader.readAsText(file)
  })
}

const normalizeHeader = (raw: string): string =>
  raw
    .replace(/^\uFEFF/, '')
    .trim()
    .replace(/^["']|["']$/g, '')
    .normalize('NFD')
    .replace(/[̀-ͯ]/g, '')
    .toLowerCase()
    .trim()

const isKnownHeader = (col: string): boolean =>
  col === 'crm' || UF_ALIASES.has(col) || getValidationSets().some((set) => set.has(col))

const validateHeaders = (headerLine: string) => {
  const delimiter = headerLine.includes(';') ? ';' : ','
  const headers = headerLine.split(delimiter).map(normalizeHeader).filter(Boolean)

  const seen = new Set<string>()
  const duplicates = headers.filter((col) => {
    if (seen.has(col)) return true
    seen.add(col)
    return false
  })
  if (duplicates.length) {
    const unique = [...new Set(duplicates)]
    return h('span', [
      'Coluna(s) duplicada(s): ',
      ...unique.flatMap((col, i) => [...(i > 0 ? [', '] : []), h('b', col)]),
      '. Cada coluna deve aparecer apenas uma vez.'
    ])
  }

  const unrecognized = headers.filter((col) => !isKnownHeader(col))
  if (unrecognized.length) {
    const validationCols = getValidationColumnNames()
    return h('span', [
      'Coluna(s) não reconhecida(s): ',
      ...unrecognized.flatMap((col, i) => [...(i > 0 ? [', '] : []), h('b', col)]),
      '.',
      h('br'),
      'As colunas aceitas são: ',
      h('b', 'crm'),
      ', ',
      h('b', 'uf'),
      ...validationCols.flatMap((col) => [', ', h('b', col)]),
      '.'
    ])
  }

  const hasCrm = headers.some((col) => col === 'crm')
  if (!hasCrm) return h('span', ['O arquivo não contém a coluna obrigatória ', h('b', 'CRM'), '.'])

  const hasUf = headers.some((col) => UF_ALIASES.has(col))
  if (!hasUf) return h('span', ['O arquivo não contém a coluna obrigatória ', h('b', 'UF'), '.'])

  const hasValidation = headers.some((col) => getValidationSets().some((set) => set.has(col)))
  if (!hasValidation) {
    const validationCols = getValidationColumnNames()
    const colNodes = validationCols.flatMap((col, i) => [
      ...(i > 0 ? [i === validationCols.length - 1 ? ' ou ' : ', '] : []),
      h('b', col)
    ])
    return h('span', ['O arquivo deve conter pelo menos uma das colunas: ', ...colNodes, '.'])
  }

  return null
}

const rejectFile = (message: string | ReturnType<typeof h>) => {
  toast.error(message)
  if (fileInput.value) fileInput.value.value = ''
}

const processFile = async (file: File) => {
  const acceptedMimeTypes = ['text/csv', 'application/vnd.ms-excel']
  const hasCsvExtension = file.name.toLowerCase().endsWith('.csv')
  const hasValidMime = !file.type || acceptedMimeTypes.includes(file.type)
  if (!hasCsvExtension || !hasValidMime) {
    rejectFile('Formato inválido. Apenas arquivos .csv são aceitos.')
    return
  }

  if (file.size > MAX_FILE_SIZE) {
    rejectFile('O arquivo excede o limite de 5MB.')
    return
  }

  try {
    const { lineCount, headerLine } = await analyzeFile(file)

    if (lineCount > MAX_LINES) {
      rejectFile(`O arquivo excede o limite de ${MAX_LINES.toLocaleString('pt-BR')} linhas.`)
      return
    }

    const headerError = validateHeaders(headerLine)
    if (headerError) {
      rejectFile(headerError)
      return
    }
  } catch {
    rejectFile('Erro ao ler o arquivo.')
    return
  }

  emit('update:modelValue', file)
}

const removeFile = () => {
  emit('update:modelValue', null)
  if (fileInput.value) fileInput.value.value = ''
}

const formatSize = (bytes: number) => {
  if (bytes === 0) return '0 Bytes'
  const k = 1024
  const sizes = ['Bytes', 'KB', 'MB', 'GB']
  const i = Math.floor(Math.log(bytes) / Math.log(k))
  return `${parseFloat((bytes / Math.pow(k, i)).toFixed(1))} ${sizes[i]}`
}

return (_ctx: any,_cache: any) => {
  const _component_v_icon = _resolveComponent("v-icon")!
  const _component_v_progress_linear = _resolveComponent("v-progress-linear")!

  return (_openBlock(), _createElementBlock("div", _hoisted_1, [
    (!_ctx.modelValue)
      ? (_openBlock(), _createElementBlock("div", {
          key: 0,
          "data-testid": "import-file-dropzone",
          role: "button",
          tabindex: "0",
          "aria-label": "Selecionar arquivo CSV",
          class: "dropzone-border relative rounded-[24px] bg-primary5/30 p-10 flex flex-col items-center justify-center transition-all cursor-pointer hover:opacity-80",
          onClick: triggerFileInput,
          onKeydown: _withKeys(triggerFileInput, ["enter"]),
          onDragover: _cache[0] || (_cache[0] = _withModifiers(() => {}, ["prevent"])),
          onDrop: _withModifiers(handleFileDrop, ["prevent"])
        }, [
          _createElementVNode("input", {
            ref_key: "fileInput",
            ref: fileInput,
            type: "file",
            "data-testid": "import-file-input",
            class: "hidden",
            accept: ".csv",
            onChange: handleFileChange
          }, null, 544),
          _createElementVNode("div", _hoisted_3, [
            _createVNode(_component_v_icon, {
              icon: _unref(mdiTrayArrowUp),
              size: "64"
            }, null, 8, ["icon"])
          ]),
          _hoisted_4,
          _hoisted_5
        ], 40, _hoisted_2))
      : (_openBlock(), _createElementBlock("div", _hoisted_6, [
          _createElementVNode("div", _hoisted_7, [
            _createVNode(_component_v_icon, {
              icon: _unref(mdiFileDelimited),
              size: "48"
            }, null, 8, ["icon"])
          ]),
          _createElementVNode("div", _hoisted_8, [
            _createElementVNode("span", _hoisted_9, _toDisplayString(_ctx.modelValue.name), 1),
            _createElementVNode("span", _hoisted_10, _toDisplayString(_ctx.uploading ? uploadProgressLabel.value : formatSize(_ctx.modelValue.size)), 1)
          ]),
          (_ctx.uploading)
            ? (_openBlock(), _createElementBlock("div", _hoisted_11, [
                _createElementVNode("span", _hoisted_12, _toDisplayString(_ctx.uploadProgress >= 99 ? 'Processando no servidor...' : `${_ctx.uploadProgress}%`), 1),
                _createVNode(_component_v_progress_linear, {
                  "model-value": _ctx.uploadProgress,
                  indeterminate: _ctx.uploadProgress >= 99,
                  color: "primary",
                  rounded: "",
                  height: "6",
                  class: "mt-1"
                }, null, 8, ["model-value", "indeterminate"]),
                _createElementVNode("p", _hoisted_13, _toDisplayString(_ctx.uploadProgress >= 99
              ? 'O arquivo foi enviado e está sendo processado. Este processo pode demorar alguns segundos para arquivos grandes.'
              : 'Não feche esta janela enquanto o envio estiver em andamento.'), 1)
              ]))
            : (_openBlock(), _createElementBlock("div", _hoisted_14, [
                _createElementVNode("button", {
                  type: "button",
                  "data-testid": "import-file-remove-button",
                  "aria-label": "Remover arquivo",
                  class: "absolute top-4 right-4 p-1.5 text-neutralLight hover:text-red-500 rounded-full transition-colors",
                  onClick: removeFile
                }, [
                  _createVNode(_component_v_icon, {
                    icon: _unref(mdiCloseCircleOutline),
                    size: "22"
                  }, null, 8, ["icon"])
                ])
              ]))
        ]))
  ]))
}
}

})